/**
 * Team Membership Service
 * Fetches user's team membership information
 * NO S3/localStorage caching - team membership can change without notice
 * Uses in-memory cache with 2-minute TTL only
 */

import { UserTeamMembership } from '../types';
import { getApiEndpoint } from '../config/amplifyConfig';

// In-memory cache for current session only (NOT persistent)
let inMemoryCache: {
  data: UserTeamMembership | null;
  timestamp: number;
  token: string;
} | null = null;

const CACHE_TTL = 2 * 60 * 1000; // 2 minutes in milliseconds

/**
 * Fetch user's team membership information
 * NO S3/localStorage caching - team membership can change without notice
 * @param token - User's authentication token
 * @param forceRefresh - Force bypass in-memory cache
 * @returns Promise<UserTeamMembership> - User's team membership data
 */
export async function fetchUserTeamMembership(
  token: string,
  forceRefresh: boolean = false
): Promise<UserTeamMembership> {
  // Check in-memory cache (short TTL only)
  if (!forceRefresh && inMemoryCache && inMemoryCache.token === token) {
    const age = Date.now() - inMemoryCache.timestamp;
    if (age < CACHE_TTL) {
      console.log(`📦 [TEAM-MEMBERSHIP] Using in-memory cache (age: ${Math.round(age/1000)}s)`);
      return inMemoryCache.data!;
    } else {
      console.log(`⏰ [TEAM-MEMBERSHIP] Cache expired (age: ${Math.round(age/1000)}s)`);
    }
  }
  
  console.log('🔄 [TEAM-MEMBERSHIP] Fetching fresh team membership data');
  
  // Call Lambda endpoint
  const url = getApiEndpoint('/crossover/my-team-membership');
  console.log('🔗 [TEAM-MEMBERSHIP] Calling endpoint:', url);
  
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': token
      }
    });
    console.log('📡 [TEAM-MEMBERSHIP] Response received:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });
  } catch (fetchError) {
    console.error('🚨 [TEAM-MEMBERSHIP] Fetch error (network/connection):', fetchError);
    throw new Error(`Network error fetching team membership: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
  }
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('🚨 [TEAM-MEMBERSHIP] Failed to fetch team membership:', {
      status: response.status,
      statusText: response.statusText,
      error: errorText,
      url: url
    });
    throw new Error(`Failed to fetch team membership (${response.status}): ${errorText || response.statusText}`);
  }
  
  const data: UserTeamMembership = await response.json();
  
  // Store in-memory cache only (short TTL)
  inMemoryCache = {
    data,
    timestamp: Date.now(),
    token
  };
  
  console.log('✅ [TEAM-MEMBERSHIP] Team membership loaded:', {
    teamsCount: data.teams.length,
    isManager: data.isManager,
    hasCentralPSAccess: data.hasCentralPSAccess,
    centralPSAccessViaAllowlist: data.centralPSAccessViaAllowlist,
    teams: data.teams.map(t => ({ name: t.teamName, role: t.role }))
  });
  
  return data;
}

/**
 * Clear in-memory team membership cache
 * Call this when user logs out or when team changes are expected
 */
export function clearTeamMembershipCache(): void {
  inMemoryCache = null;
  console.log('🗑️ [TEAM-MEMBERSHIP] Cache cleared');
}

/**
 * Force refresh team membership (bypass cache)
 * Use when team membership may have changed
 */
export async function refreshTeamMembership(token: string): Promise<UserTeamMembership> {
  console.log('🔄 [TEAM-MEMBERSHIP] Force refreshing team membership');
  return fetchUserTeamMembership(token, true);
}

/**
 * Get cached team membership age in seconds
 * Returns null if no cache exists
 */
export function getCacheAge(): number | null {
  if (!inMemoryCache) {
    return null;
  }
  return Math.round((Date.now() - inMemoryCache.timestamp) / 1000);
}


